import { Link } from "@tanstack/react-router";
import { CalendarDays, ListChecks, SquareParking, Users } from "lucide-react";
import { copy } from "../../lib/copy";
import { useKeyboardOpen } from "../../lib/useKeyboardOpen";

/**
 * The bottom tab bar. Fixed to the viewport, so it owns the home indicator —
 * `body` deliberately carries no safe-area padding (see tokens.css).
 *
 * On paper, not surface: it is the floor of the page, and the floating primary
 * action is what sits on top of things. Active is a soft accent pill behind the
 * icon, accent label at 600 — shape and weight as well as colour, and
 * `aria-current` for anyone not seeing any of it.
 *
 * Hidden while the keyboard is up, with the primary action (see there).
 *
 * No animation. The motion budget is four and the task list spends all four.
 */
const TABS = [
  { to: "/tasks", label: copy.app.tabs.tasks, Icon: ListChecks },
  { to: "/calendar", label: copy.app.tabs.calendar, Icon: CalendarDays },
  { to: "/parking", label: copy.app.tabs.parking, Icon: SquareParking },
  { to: "/people", label: copy.app.tabs.people, Icon: Users },
] as const;

export function TabBar() {
  const keyboard = useKeyboardOpen();
  if (keyboard) return null;

  return (
    <nav
      aria-label={copy.app.name}
      className="fixed inset-x-0 bottom-0 z-10 border-t border-line bg-paper pb-[env(safe-area-inset-bottom)]"
    >
      <ul className="mx-auto flex h-tab-bar max-w-md items-stretch">
        {TABS.map(({ to, label, Icon }) => (
          <li key={to} className="flex-1">
            <Link
              to={to}
              className="group flex h-full flex-col items-center justify-center gap-0.5 text-muted aria-[current=page]:font-semibold aria-[current=page]:text-accent"
              activeProps={{ "aria-current": "page" }}
            >
              <span className="grid h-7.5 w-14 place-items-center rounded-pill group-aria-[current=page]:bg-accent-soft">
                <Icon aria-hidden className="size-6" strokeWidth={1.75} />
              </span>
              <span className="text-meta">{label}</span>
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  );
}
